<script setup lang="ts">
import type { AgentShortsSectionProps } from '.';

import { useAgentShorts } from '~/composables/agentShorts';
import { useEvents } from '~/composables/events/useEvents';
import { useOpenShortsFeed } from '~/composables/shorts';
import { MixpanelProps } from '~/core/service/mixpanel';
import { cn } from '~/utils';

import { Skeleton } from '~/components/ui/skeleton';
import { VideoCardHomeFeed } from '~/components/VideoCard';

import IconMaterialSymbolsLightKeyboardArrowDown from '~icons/material-symbols-light/keyboard-arrow-down';
import IconMaterialSymbolsSlowMotionVideo from '~icons/material-symbols/slow-motion-video-sharp';

import {
	AGENT_SHORTS_CARD_CLASS,
	AGENT_SHORTS_GRID_CLASS,
	AGENT_SHORTS_SKELETON_COUNT
} from './grid';
import SectionSkeleton from './SectionSkeleton.vue';

const SECTION_NAME = 'agent profile shorts';
const VIEW_MORE_CTA_TEXT = 'Ver mais';

const props = defineProps<AgentShortsSectionProps>();

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();
const { openShortsFeed } = useOpenShortsFeed();

const agentShorts = useAgentShorts(() => props.agentSlug);
const { items, isLoading, isLoadingMore, isLoaded, hasMore, hasVideos } =
	agentShorts;

/**
 * A seção não renderiza quando o corretor não tem vídeo; enquanto a request não
 * responde, o skeleton ocupa o lugar para não haver salto de layout.
 */
const isInitialLoading = computed(() => !isLoaded.value || isLoading.value);

const viewMoreClickCount = ref(0);

function handleCardClick(index: number): void {
	const item = items.value[index];
	if (!item) return;

	openShortsFeed({
		at: item.property.commercialId,
		page: agentShorts.snapshot(index)
	});
}

async function handleViewMoreClick(): Promise<void> {
	viewMoreClickCount.value++;

	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: SECTION_NAME,
		[MixpanelProps.DESTINATION_URL]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: VIEW_MORE_CTA_TEXT,
		[MixpanelProps.CLICK_COUNT]: viewMoreClickCount.value
	});

	await agentShorts.loadMore();
}

onMounted(() => {
	void agentShorts.load();
});
const hasContent = computed<boolean>(() => hasVideos.value);

defineExpose({ hasContent });
</script>

<template>
	<SectionSkeleton v-if="isInitialLoading" :class="props.class" />

	<section
		v-else-if="hasVideos"
		:class="
			cn(
				'w-full border-b border-white-dark-3 bg-white py-8 md:py-12',
				props.class
			)
		"
		data-testid="agent-shorts-section"
		:aria-busy="isLoadingMore"
	>
		<header class="mb-4 flex items-center gap-3 md:mb-6 md:gap-3.5">
			<IconMaterialSymbolsSlowMotionVideo
				class="size-7 shrink-0 text-primary"
				aria-hidden="true"
			/>
			<h2
				class="font-matter-sq text-xl font-medium leading-[150%] text-primary lg:text-[32px] lg:font-normal lg:tracking-[-0.04rem]"
			>
				<span class="md:hidden">Shorts</span>
				<span class="hidden md:inline">Imóveis em Shorts</span>
			</h2>
		</header>

		<div :class="AGENT_SHORTS_GRID_CLASS">
			<VideoCardHomeFeed
				v-for="(item, index) in items"
				:key="item.property.commercialId"
				:property="item.property"
				:thumbnail-url="item.thumbnailUrl"
				:video-url="item.videoUrl"
				:index="index"
				:section-name="SECTION_NAME"
				card-type="shorts card"
				:img-eager-count="4"
				:img-preload-count="2"
				player-root-margin="200px"
				:prefetch-variants="item.prefetchVariants"
				layout="agent-profile"
				@click="handleCardClick"
			/>

			<Skeleton
				v-for="skeletonIndex in isLoadingMore ? AGENT_SHORTS_SKELETON_COUNT : 0"
				:key="`agent-shorts-more-skeleton-${skeletonIndex}`"
				is-active
				variant="white"
				:class="AGENT_SHORTS_CARD_CLASS"
				data-testid="agent-shorts-more-card-skeleton"
			/>
		</div>

		<div v-if="hasMore" class="mt-6">
			<Button
				variant="secondary"
				size="sm"
				class="h-12 w-full gap-2 font-medium md:w-[148px]"
				data-testid="agent-shorts-view-more"
				:disabled="isLoadingMore"
				@click="handleViewMoreClick"
			>
				<span>{{ VIEW_MORE_CTA_TEXT }}</span>
				<IconMaterialSymbolsLightKeyboardArrowDown
					class="size-5"
					aria-hidden="true"
				/>
			</Button>
		</div>
	</section>
</template>
